import { useNavigate } from 'react-router-dom';
import { Compass, Home, Sparkles, ArrowRight } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import SiteFooter from '../components/SiteFooter';

const QUICK_LINKS = [
  { label: 'Home', to: '/' },
  { label: 'AI Builder', to: '/build' },
  { label: 'Pricing', to: '/pricing' },
  { label: 'Contact', to: '/contact' },
  { label: 'About', to: '/about' },
];

export default function NotFoundPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#030303] text-white font-sans relative overflow-hidden flex flex-col">
      <div
        aria-hidden
        className="absolute -top-40 -left-40 w-[720px] h-[720px] rounded-full blur-3xl opacity-25 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #00F0FF 0%, transparent 70%)' }}
      />
      <div
        aria-hidden
        className="absolute -bottom-40 -right-40 w-[720px] h-[720px] rounded-full blur-3xl opacity-25 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #FF007F 0%, transparent 70%)' }}
      />

      <header className="relative z-10 border-b border-white/[0.06] bg-black/40 backdrop-blur-xl">
        <div className="mx-auto max-w-6xl px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} data-testid="notfound-brand-link">
            <BrandLogo size={30} />
          </button>
        </div>
      </header>

      <main className="relative z-10 flex-1 flex items-center justify-center px-6 py-16">
        <div className="text-center max-w-2xl">
          <div
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[11px] font-mono uppercase tracking-[0.25em] text-white/60 mb-8"
            data-testid="notfound-badge"
          >
            <Compass size={11} /> Off the map
          </div>
          <h1
            className="font-display font-black text-[120px] md:text-[180px] leading-none tracking-tight"
            style={{
              background:
                'linear-gradient(180deg, #ffffff 0%, rgba(255,255,255,0.4) 60%, rgba(255,255,255,0.1) 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            404
          </h1>
          <h2 className="mt-2 text-3xl md:text-4xl font-display font-black tracking-tight">
            You&rsquo;ve wandered off the world map.
          </h2>
          <p className="mt-4 text-lg text-white/60">
            The page you were looking for doesn&rsquo;t exist — or maybe it hasn&rsquo;t been built
            yet. Either way, our AI can probably build it for you in 30 seconds.
          </p>

          <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
            <button
              onClick={() => navigate('/')}
              data-testid="notfound-home-btn"
              className="rounded-full px-5 py-2.5 text-sm font-semibold text-black flex items-center gap-2"
              style={{ background: 'linear-gradient(90deg, #00F0FF 0%, #FF007F 100%)' }}
            >
              <Home size={14} /> Back home
            </button>
            <button
              onClick={() => navigate('/build')}
              data-testid="notfound-build-btn"
              className="rounded-full border border-white/15 hover:bg-white/[0.05] px-5 py-2.5 text-sm text-white transition flex items-center gap-2"
            >
              <Sparkles size={14} /> Build something instead
            </button>
          </div>

          <div className="mt-12">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/40 mb-3">
              Or try one of these
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {QUICK_LINKS.map((l) => (
                <button
                  key={l.label}
                  onClick={() => navigate(l.to)}
                  data-testid={`notfound-quick-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className="rounded-full border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] hover:border-white/20 text-white/80 hover:text-white text-[12.5px] px-3.5 py-1.5 transition inline-flex items-center gap-1.5"
                >
                  {l.label}
                  <ArrowRight size={11} className="text-white/40" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
